// SKU Cache Service for NLFX Cable Builder
// Caches the deterministic base SKU per configuration; appends a fresh timestamp on each
// generate call so every cart line / product gets a unique SKU (including duplicate configs).

class SkuCacheService {
  constructor() {
    this.skuCache = new Map();
    this.sessionId = this.generateSessionId();
    this.sessionTimestamp = this.generateSessionTimestamp();
  }

  // Generate a unique session ID
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Generate a consistent timestamp for this session
  generateSessionTimestamp() {
    return new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '-');
  }

  // Create a cache key from configuration
  createCacheKey(config) {
    if (!config) return null;
    
    // Create a normalized version of the config for consistent hashing
    const normalizedConfig = {
      type: config.type,
      style: config.style,
      length: config.length,
      connectors: config.connectors,
      hybridConnectors: config.hybridConnectors,
      finishing: config.finishing,
      wireGauge: config.wireGauge,
      quality: config.quality
    };
    
    // Convert to JSON string and hash it
    const configString = JSON.stringify(normalizedConfig);
    return this.hashString(configString);
  }

  // Simple hash function for configuration
  hashString(str) {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(36);
  }

  // Get cached base SKU or generate new one (without timestamp)
  getOrGenerateBaseSku(config, generateSkuFunction) {
    if (!config || !generateSkuFunction) {
      return generateSkuFunction ? generateSkuFunction(config) : null;
    }

    const cacheKey = this.createCacheKey(config);
    if (!cacheKey) {
      return generateSkuFunction(config);
    }

    // Check if we have a cached base SKU for this configuration
    if (this.skuCache.has(cacheKey)) {
      const cachedBaseSku = this.skuCache.get(cacheKey);
      console.log('SKU Cache: Using cached base SKU for configuration:', cachedBaseSku);
      return cachedBaseSku;
    }

    // Generate new base SKU and cache it
    const newBaseSku = generateSkuFunction(config);
    if (newBaseSku) {
      this.skuCache.set(cacheKey, newBaseSku);
      console.log('SKU Cache: Generated and cached new base SKU:', newBaseSku);
    }

    return newBaseSku;
  }

  // Force generate a new base SKU (for when finishing options change)
  forceGenerateBaseSku(config, generateSkuFunction) {
    if (!config || !generateSkuFunction) {
      return generateSkuFunction ? generateSkuFunction(config) : null;
    }

    const cacheKey = this.createCacheKey(config);
    const newBaseSku = generateSkuFunction(config);
    
    if (newBaseSku && cacheKey) {
      this.skuCache.set(cacheKey, newBaseSku);
      console.log('SKU Cache: Force generated and cached new base SKU:', newBaseSku);
    }

    return newBaseSku;
  }

  // Per-cart-line timestamp (same format as skuService.generateTimestamp)
  nextSkuTimestamp() {
    return new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '-');
  }

  // Get cached SKU or generate new one (unique timestamp each time this is called)
  getOrGenerateSku(config, generateSkuFunction) {
    const baseSku = this.getOrGenerateBaseSku(config, generateSkuFunction);
    if (!baseSku) return null;

    const skuWithTimestamp = `${baseSku}--${this.nextSkuTimestamp()}`;
    console.log('SKU Cache: Generated SKU with per-cable timestamp:', skuWithTimestamp);
    return skuWithTimestamp;
  }

  // Force generate a new SKU (for when finishing options change)
  forceGenerateSku(config, generateSkuFunction) {
    const baseSku = this.forceGenerateBaseSku(config, generateSkuFunction);
    if (!baseSku) return null;

    const skuWithTimestamp = `${baseSku}--${this.nextSkuTimestamp()}`;
    console.log('SKU Cache: Force generated SKU with per-cable timestamp:', skuWithTimestamp);
    return skuWithTimestamp;
  }

  // Clear cache for a specific configuration
  clearCacheForConfig(config) {
    if (!config) return;
    
    const cacheKey = this.createCacheKey(config);
    if (cacheKey && this.skuCache.has(cacheKey)) {
      this.skuCache.delete(cacheKey);
      console.log('SKU Cache: Cleared cache for configuration');
    }
  }

  // Clear entire cache
  clearCache() {
    this.skuCache.clear();
    console.log('SKU Cache: Cleared entire cache');
  }

  // Get cache statistics
  getCacheStats() {
    return {
      sessionId: this.sessionId,
      sessionTimestamp: this.sessionTimestamp,
      cacheSize: this.skuCache.size,
      cachedBaseSkus: Array.from(this.skuCache.values())
    };
  }

  // Check if a configuration has a cached SKU
  hasCachedSku(config) {
    if (!config) return false;
    
    const cacheKey = this.createCacheKey(config);
    return cacheKey ? this.skuCache.has(cacheKey) : false;
  }

  // Get cached base SKU without generating new one
  getCachedBaseSku(config) {
    if (!config) return null;
    
    const cacheKey = this.createCacheKey(config);
    return cacheKey ? this.skuCache.get(cacheKey) || null : null;
  }

  // Get cached SKU with timestamp without generating new one (timestamp is not stored; prefer getOrGenerateSku)
  getCachedSku(config) {
    const baseSku = this.getCachedBaseSku(config);
    if (!baseSku) return null;

    return `${baseSku}--${this.sessionTimestamp}`;
  }
}

// Create singleton instance
const skuCacheService = new SkuCacheService();

export default skuCacheService;
